import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPremiumAccess } from "@/lib/subscription";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Lock, Apple } from "lucide-react";
import MacroBars from "@/components/food/MacroBars";
import AiQuickLog from "@/components/food/AiQuickLog";
import FoodSearchModal from "@/components/food/FoodSearchModal";

const MEAL_SLOTS = [
  { key: "breakfast", label: "Breakfast" },
  { key: "lunch", label: "Lunch" },
  { key: "dinner", label: "Dinner" },
  { key: "snack", label: "Snacks" },
];

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function FoodLog() {
  const { user } = useAuth();
  const isPremium = hasPremiumAccess(user);
  const [date, setDate] = useState(fmtDate(new Date()));
  const [day, setDay] = useState(null);
  const [modalMeal, setModalMeal] = useState(null); // meal slot the modal adds to

  const load = async (d = date) => {
    try {
      const r = await api.get("/food/log", { params: { date: d } });
      setDay(r.data);
    } catch {}
  };
  useEffect(() => {
    load(date);
  }, [date]);

  const shiftDay = (delta) => {
    const d = new Date(date + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setDate(fmtDate(d));
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/food/log/${id}`);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const isToday = date === fmtDate(new Date());
  const entries = day?.entries || [];
  const byMeal = (slot) => entries.filter((e) => e.meal === slot);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-blaze">/ Nutrition</div>
          <h1 className="font-heading text-5xl uppercase mt-1">Food log.</h1>
          {!isPremium && (
            <p className="text-[11px] uppercase tracking-widest text-yellow-500 mt-2 inline-flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Food tracking is a Premium feature
            </p>
          )}
        </div>
        {/* Date navigation */}
        <div className="flex items-center gap-2" data-testid="food-date-nav">
          <button onClick={() => shiftDay(-1)} data-testid="food-date-prev" className="ghost-btn p-2" aria-label="Previous day">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input
            type="date"
            data-testid="food-date-input"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm text-sm focus:border-blaze focus:outline-none"
          />
          <button
            onClick={() => shiftDay(1)}
            data-testid="food-date-next"
            disabled={isToday}
            className="ghost-btn p-2 disabled:opacity-40"
            aria-label="Next day"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!isPremium ? (
        <div className="obs-card border-yellow-500/30 bg-yellow-500/5 text-center py-14" data-testid="food-premium-gate">
          <Lock className="h-10 w-10 text-yellow-500 mx-auto mb-4" />
          <h3 className="font-heading text-3xl uppercase mb-2">Track every bite</h3>
          <p className="text-zinc-400 mb-6 max-w-md mx-auto">
            Search millions of foods, log meals in seconds with AI, and watch your macros stack up against your targets — a Premium feature.
          </p>
          <Link to="/pricing" data-testid="food-upgrade-cta" className="blaze-btn px-6 py-2.5 text-xs uppercase tracking-widest">
            Upgrade to Premium
          </Link>
        </div>
      ) : (
        <>
          <MacroBars totals={day?.totals} targets={day?.targets} />

          <AiQuickLog date={date} onLogged={() => load()} />

          {MEAL_SLOTS.map(({ key, label }) => {
            const slotEntries = byMeal(key);
            const kcal = slotEntries.reduce((s, e) => s + (e.calories || 0), 0);
            return (
              <div key={key} className="obs-card" data-testid={`meal-section-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-heading text-2xl uppercase">{label}</h3>
                    {slotEntries.length > 0 && (
                      <div className="text-[11px] text-zinc-500 font-data uppercase tracking-widest">{Math.round(kcal)} kcal</div>
                    )}
                  </div>
                  <button
                    data-testid={`add-food-${key}`}
                    onClick={() => setModalMeal(key)}
                    className="ghost-btn px-3 py-1.5 text-[10px] inline-flex items-center gap-1.5"
                  >
                    <Plus className="h-3 w-3" /> Add food
                  </button>
                </div>
                {slotEntries.length === 0 ? (
                  <p className="text-zinc-600 text-sm">Nothing logged.</p>
                ) : (
                  <div className="divide-y divide-obsidian-600/50">
                    {slotEntries.map((e) => (
                      <div key={e.id} className="flex items-center justify-between gap-2 py-2" data-testid={`food-entry-${e.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm truncate">
                            {e.name}
                            {e.brand && <span className="text-zinc-500"> · {e.brand}</span>}
                          </div>
                          <div className="text-[11px] text-zinc-500">{e.qty_desc}</div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="font-data text-xs text-zinc-400">
                            {e.calories} kcal · <span className="text-blaze">{e.protein_g}P</span> {e.carbs_g}C {e.fat_g}F
                          </div>
                          <button
                            data-testid={`delete-entry-${e.id}`}
                            onClick={() => removeEntry(e.id)}
                            className="text-zinc-500 hover:text-red-400"
                            aria-label="Delete entry"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {entries.length === 0 && (
            <div className="text-center text-zinc-600 text-sm py-4 inline-flex items-center gap-2 w-full justify-center">
              <Apple className="h-4 w-4" /> Tip: use AI quick-log above to log a whole meal in one sentence.
            </div>
          )}
        </>
      )}

      {modalMeal && (
        <FoodSearchModal
          date={date}
          meal={modalMeal}
          onClose={() => setModalMeal(null)}
          onLogged={() => {
            setModalMeal(null);
            load();
          }}
        />
      )}
    </div>
  );
}
